import { useEffect, useMemo, useRef, useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import {
  userLoginApi,
  userMasterApi,
  userRoleApi,
} from "../../api/masterApi";
import "../../components/MasterCrud.css";
import "./CreateLogin.css";

function generatePassword(length = 10) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < length; i += 1) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

const emptyForm = () => ({
  user_role_id: "",
  reference_id: "",
  name: "",
  phone: "",
  email: "",
  password: generatePassword(),
  status: true,
});

export default function CreateLogin() {
  const [searchParams] = useSearchParams();
  const prefillApplied = useRef(false);
  const [form, setForm] = useState(emptyForm);
  const [rows, setRows] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const selectedUserRole = useMemo(
    () => userRoles.find((t) => String(t.user_role_id) === String(form.user_role_id)),
    [userRoles, form.user_role_id]
  );

  const userOptions = useMemo(() => {
    if (!selectedUserRole) return [];
    return users
      .filter(
        (user) =>
          String(user.user_role_id) === String(selectedUserRole.user_role_id)
      )
      .map((user) => {
        const name = [user.user_title, user.first_name, user.last_name]
          .filter(Boolean)
          .join(" ");
        return {
          value: user.user_id,
          label: name || "User",
          name,
          phone: user.phone_numbers?.[0] || "",
          email: user.email_addresses?.[0] || "",
        };
      });
  }, [selectedUserRole, users]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [logins, roles, userList] = await Promise.all([
        userLoginApi.list(),
        userRoleApi.list(),
        userMasterApi.list(),
      ]);
      setRows(logins);
      setUserRoles((roles || []).filter((t) => Number(t.status) === 1));
      setUsers(userList || []);
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prefillApplied.current || users.length === 0) return;

    const userId = searchParams.get("user_id");
    const userRoleId = searchParams.get("user_role_id");
    if (!userId || !userRoleId) {
      prefillApplied.current = true;
      return;
    }

    const user = users.find(
      (item) =>
        String(item.user_id) === String(userId) &&
        String(item.user_role_id) === String(userRoleId)
    );
    if (!user) return;

    setForm((previous) => ({
      ...previous,
      user_role_id: String(user.user_role_id),
      reference_id: String(user.user_id),
      name: [user.user_title, user.first_name, user.last_name]
        .filter(Boolean)
        .join(" "),
      phone: user.phone_numbers?.[0] || "",
      email: user.email_addresses?.[0] || "",
    }));
    prefillApplied.current = true;
  }, [searchParams, users]);

  const resetForm = () => {
    setForm(emptyForm());
    setEditingId(null);
  };

  const handleUserRoleChange = (value) => {
    setForm((prev) => ({
      ...prev,
      user_role_id: value,
      reference_id: "",
      name: "",
      phone: "",
      email: "",
    }));
  };

  const handleUserChange = (value) => {
    const selected = userOptions.find((u) => String(u.value) === String(value));
    setForm((prev) => ({
      ...prev,
      reference_id: value,
      name: selected?.name || "",
      phone: selected?.phone || "",
      email: selected?.email || "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.user_role_id) {
      showToast("User Role is required", "error");
      return;
    }
    if (!form.reference_id) {
      showToast("User is required", "error");
      return;
    }
    if (!form.name.trim()) {
      showToast("Name is required", "error");
      return;
    }
    if (!form.phone.trim()) {
      showToast("Phone No is required", "error");
      return;
    }
    if (!form.password.trim()) {
      showToast("Password is required", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        user_role_id: Number(form.user_role_id),
        reference_id: Number(form.reference_id),
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        password: form.password,
        status: form.status ? 1 : 0,
      };

      const body = JSON.stringify(payload);
      if (editingId) {
        await userLoginApi.update(editingId, body, { json: true });
        showToast("Login updated successfully");
      } else {
        await userLoginApi.create(body, { json: true });
        showToast("Login created successfully");
      }
      resetForm();
      await loadData();
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.login_id);
    setForm({
      user_role_id: String(item.user_role_id || ""),
      reference_id: String(item.reference_id || ""),
      name: item.name || "",
      phone: item.phone || "",
      email: item.email || "",
      password: item.password_plain || generatePassword(),
      status: Number(item.status) === 1,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm("Are you sure you want to delete this login?");
    if (!confirmed) return;
    try {
      await userLoginApi.remove(id);
      showToast("Login deleted successfully");
      if (editingId === id) resetForm();
      await loadData();
    } catch (error) {
      showToast(error.message, "error");
    }
  };

  return (
    <div className="master-page">
      <div className="master-card create-login-card">
        <div className="master-card__header">
          <h2>Login Credentials</h2>
          <p>Create login access for users from User Master. Password is system generated.</p>
        </div>

        <form className="create-login-form" onSubmit={handleSubmit}>
          <div className="create-login-form__field">
            <label htmlFor="user_role_id">
              User Role <span className="create-login-form__req">*</span>
            </label>
            <select
              id="user_role_id"
              value={form.user_role_id}
              onChange={(e) => handleUserRoleChange(e.target.value)}
            >
              <option value="">Select user role</option>
              {userRoles.map((role) => (
                <option key={role.user_role_id} value={role.user_role_id}>
                  {role.user_role}
                </option>
              ))}
            </select>
          </div>

          <div className="create-login-form__field">
            <label htmlFor="reference_id">
              User <span className="create-login-form__req">*</span>
            </label>
            <select
              id="reference_id"
              value={form.reference_id}
              onChange={(e) => handleUserChange(e.target.value)}
              disabled={!form.user_role_id}
            >
              <option value="">Select user</option>
              {userOptions.map((user) => (
                <option key={user.value} value={user.value}>
                  {user.label}
                </option>
              ))}
            </select>
          </div>

          <div className="create-login-form__field">
            <label htmlFor="name">
              Name <span className="create-login-form__req">*</span>
            </label>
            <input
              id="name"
              type="text"
              placeholder="Enter name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </div>

          <div className="create-login-form__field">
            <label htmlFor="phone">
              Phone No <span className="create-login-form__req">*</span>
            </label>
            <input
              id="phone"
              type="text"
              placeholder="Phone number"
              value={form.phone}
              onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
            />
          </div>

          <div className="create-login-form__field">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
            />
          </div>

          <div className="create-login-form__field">
            <label htmlFor="password">Password (System Generated)</label>
            <div className="create-login-form__password-row">
              <input id="password" type="text" value={form.password} readOnly />
              <button
                type="button"
                className="master-btn master-btn--ghost"
                onClick={() =>
                  setForm((prev) => ({ ...prev, password: generatePassword() }))
                }
              >
                Regenerate
              </button>
            </div>
          </div>

          <div className="create-login-form__field">
            <label className="master-form__check">
              <input
                type="checkbox"
                checked={form.status}
                onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.checked }))}
              />
              Active
            </label>
          </div>

          <div className="create-login-form__actions">
            {editingId && (
              <button type="button" className="master-btn master-btn--ghost" onClick={resetForm}>
                Cancel
              </button>
            )}
            <button type="submit" className="master-btn master-btn--primary" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>

      <div className="master-card">
        <div className="master-card__header">
          <h3>Saved Logins</h3>
          <p>{loading ? "Loading..." : `${rows.length} logins`}</p>
        </div>

        <div className="master-table-wrap">
          <table className="master-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Login ID</th>
                <th>User Role</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Password</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="master-table__empty">
                    No logins yet. Create one above.
                  </td>
                </tr>
              )}
              {rows.map((item, index) => (
                <tr key={item.login_id}>
                  <td>{index + 1}</td>
                  <td>{item.login_id}</td>
                  <td>{item.user_role || "—"}</td>
                  <td>{item.name}</td>
                  <td>{item.phone}</td>
                  <td>{item.email || "—"}</td>
                  <td>{item.password_plain || "—"}</td>
                  <td>{Number(item.status) === 1 ? "Active" : "Inactive"}</td>
                  <td>
                    <div className="master-table__actions">
                      <button
                        type="button"
                        className="master-btn master-btn--edit"
                        onClick={() => handleEdit(item)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="master-btn master-btn--delete"
                        onClick={() => handleDelete(item.login_id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
